/** @format */

import { Link, useParams } from "react-router-dom";
import { Locale } from "../../features/i18n/init";
import HomePageImg from "../../assets/images/HomePageImg.svg?react";
import MagicIcon from "../../assets/icons/MagicIcon.svg?react";
import SearchIcon from "../../assets/icons/SearchIcon.svg?react";
import ArrowLeftIcon from "../../assets/icons/ArrowLeftIcon.svg?react";
import { Aggregation } from "@question-bank/shared";
import { useState, useEffect } from "react";
import { useBrand } from "../../hooks/useBrand";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { searchQuestions } from "../../api/questions";
import MathematicsIcon from "../../assets/icons/subjects/subject-icon_mathematics.svg?react";
import BusinessIcon from "../../assets/icons/subjects/subject-icon_business.svg?react";
import BlendedLearningIcon from "../../assets/icons/BlendedLearning.svg?react";
import { useTranslation } from "react-i18next";

const headerLinkStyle =
	"flex flex-row h-[48px] w-full sm:w-fit sm:flex-1 lg:flex-none sm:px-6 xl:w-[205px] rounded-[6px] border py-[6px] xl:px-[12px] font-roboto text-[16px] leading-[150%] items-center justify-center font-normal gap-2";

const subjectLinkStyle =
	"flex flex-col gap-2.5 font-lato text-[14px] xl:text-base leading-175% items-center text-center w-full max-w-[140px]";
const subjectIconStyle = "size-8 xl:size-10 text-white";
const subjectIconWrapStyle =
	"flex items-center justify-center size-15 xl:size-18 rounded-full bg-(--public-color-foundational-primary)";

const SUBJECT_ICON_MAP: Record<string, { icon: typeof BusinessIcon; translationKey: string }> = {
	business: { icon: BusinessIcon, translationKey: "subjects.business" },
	mathematics: { icon: MathematicsIcon, translationKey: "subjects.mathematics" },
};

export const PublicLandingPage = () => {
	const { locale } = useParams<{ locale: Locale }>();
	const { t } = useTranslation();
	const { brand } = useBrand();
	const [subjectAggregations, setSubjectAggregations] = useState<Aggregation[]>([]);

	useDocumentTitle(t("pageTitles.home"));

	useEffect(() => {
		let isCancelled = false;
		searchQuestions(brand, {}).then((response) => {
			if (!isCancelled) {
				setSubjectAggregations(response.aggregations?.subject ?? []);
			}
		});
		return () => {
			isCancelled = true;
		};
	}, [brand]);

	const sortedSubjects = [...subjectAggregations]
		.filter((agg) => SUBJECT_ICON_MAP[agg.key])
		.sort((a, b) => b.count - a.count);

	return (
		<div className="px-4 pt-2 pb-5 xl:pr-12.5 xl:pb-10 xl:pl-5 xl:py-0">
			<header className="flex flex-col lg:flex-row items-center lg:justify-between w-full h-auto lg:min-h-72.5 rounded-[10px] border border-(--public-color-border-default) p-6 xl:px-10 xl:py-7.75 gap-6 xl:gap-10 bg-(--public-color-filter-panel-bg)">
				<div className="flex flex-col items-center text-center md:items-start md:text-left gap-7.25 min-w-0">
					<h1 className="font-lato font-bold text-[28px] xl:text-[36px] leading-[100%] text-(--public-color-text-default)">
						{t("homePage.welcomeTitle")}
					</h1>
					<span className="font-lato font-normal text-[16px] leading-[175%] text-(--public-color-text-default)">
						{t("homePage.subtitle")}
					</span>

					<div className="flex flex-col sm:flex-row gap-4 xl:gap-7.75 w-full lg:w-auto min-w-0">
						<Link
							to=""
							onClick={(e) => {
								e.preventDefault();
								e.currentTarget.focus();
							}}
							aria-disabled="true"
							className={`${headerLinkStyle} relative group bg-(--public-color-menu-label) border-(--public-color-menu-label) text-white cursor-not-allowed`}
						>
							<MagicIcon className="text-primary" />
							{t("homePage.randomGenerateLink")}
							<span
								role="tooltip"
								className="pointer-events-none absolute -top-9 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-white text-(--public-color-text-default) font-bold text-[16px] px-2 py-2 shadow-lg z-10 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
							>
								Coming Soon
							</span>
						</Link>

						<Link
							to={`/${locale}/search`}
							className={`${headerLinkStyle} bg-white border-(--public-color-text-default) text-(--public-color-text-default)`}
						>
							<SearchIcon className="text-primary size-5" />
							{t("homePage.browseQuestionsLink")}
						</Link>
					</div>
				</div>

				<div className="min-w-0 shrink-0 w-full lg:w-92.5">
					<HomePageImg className="w-full h-auto" />
				</div>
			</header>

			<div className="flex flex-col mt-6 md:mt-2 h-auto w-full">
				<h1 className="font-lato font-black text-[24px] leading-[150%] text-(--public-color-text-default)">
					{t("homePage.subjectExplorer")}
				</h1>

				<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-1">
					<span className="font-lato font-normal text-[16px] leading-[175%] text-(--public-color-text-default) max-w-md">
						{t("homePage.selectDepartment")}
					</span>

					{sortedSubjects.length > 9 && (
						<Link
							to={`/${locale}/search`}
							className="flex flex-row items-center gap-1.25 font-lato text-[16px] font-normal leading-[175%] text-black"
						>
							{t("homePage.viewMoreSubjects")}
							<ArrowLeftIcon className="size-6 rotate-180" />
						</Link>
					)}
				</div>

				<div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-x-6 gap-y-8 mt-5 justify-items-center">
					{sortedSubjects.map(({ key, count }) => {
						const { icon: Icon, translationKey } = SUBJECT_ICON_MAP[key];
						return (
							<Link to={`/${locale}/search?s=${encodeURIComponent(key)}`} key={key} className={subjectLinkStyle}>
								<div className={subjectIconWrapStyle}>
									<Icon className={subjectIconStyle} />
								</div>
								<span>
									{t(translationKey)} ({count})
								</span>
							</Link>
						);
					})}
				</div>
			</div>

			<div className="flex flex-col md:flex-row items-stretch md:items-start justify-between gap-8 mt-6 md:mt-2">
				<div className="flex flex-col gap-1.75 w-full md:w-174.25 bg-white border border-(--public-color-border-default) rounded-[10px] py-5 px-7.5">
					<h1 className="font-lato font-black text-[20px] xl:text-[24px] leading-[100%] text-(--public-color-text-default)">
						{t("homePage.comingSoon")}
					</h1>
					<span className="font-lato font-normal text-[16px]">{t("homePage.mostPopular")}</span>
					<span className="font-lato font-normal text-[16px]">{t("homePage.instantPaper")}</span>
					<span className="font-lato font-bold text-[18px] xl:text-[20px]">{t("homePage.stayTuned")}</span>
				</div>

				<div className="bg-white flex flex-col gap-2.75 border border-(--public-color-border-default) rounded-[10px] w-full md:w-100 ha-auto md:h-50 px-4.5 pt-4.5 pb-3.75 items-center justify-center">
					<BlendedLearningIcon />

					<p className="font-lato font-semibold text-[20px] leading-[150%] text-center">
						{t("homePage.freeEducationalResources")}
					</p>
					<a
						href="https://filter.resources.wjec.co.uk/en"
						target="_blank"
						rel="noopener noreferrer"
						className="flex flex-row gap-2 font-lato text-[16px] items-center text-black leading-[175%]"
					>
						{t("homePage.browseResources")}
						<ArrowLeftIcon className="size-5 rotate-180" />
					</a>
				</div>
			</div>
		</div>
	);
};
